import { SxProps, Theme } from '@mui/material';
import { sanitizeDateTimezone } from '@predium/utils';
import dayjs, { Dayjs } from 'dayjs';
import { clamp } from 'lodash';
import isNil from 'lodash/isNil';
import { mergeSx } from '@/utils/cssStyles';

export const validationWarningSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'warning.main' },
    '&:hover fieldset': { borderColor: 'warning.main' },
    '&.Mui-focused fieldset': { borderColor: 'warning.main' },
  },
  '& .MuiFormHelperText-root, & .MuiFormHelperText-root .MuiTypography-root': {
    color: 'warning.dark',
  },
};

/**
 * Prepends `validationWarningSx` to the component's `sx` when `warningText` is set,
 * so callers only need to pass `warningText` without manually spreading warning styles.
 */
export const applyWarningSx = (warningText: string | undefined, sx?: SxProps<Theme>): SxProps<Theme> | undefined => {
  if (!warningText) return sx;
  return mergeSx(validationWarningSx, sx);
};

/**
   * Fixes the timezone issue with the dates being stored in local time instead of UTC.
   *
   * Returns the date in a UTC format with the time zeroed instead of including the local time.
    eg.:
      before: 2025-02-28T23:00:00+00:00
      after: 2025-03-01T00:00:00+00:00
  */
export const getUtcValue = (nextDate: Date) => {
  const date = dayjs(nextDate);

  // Build the date directly instead of parsing a formatted string: unpadded strings like
  // `2-1-1` (a partially typed year) fall through dayjs' parser to `new Date()` and produce
  // garbage, and `Date.UTC` would map years 0-99 to 19xx.
  const fixedValue = new Date(0);
  fixedValue.setUTCFullYear(date.year(), date.month(), date.date());

  return fixedValue;
};

/**
 * Normalises anything the form may hold for a date field (GraphQL string, `Date`, `Dayjs`) to the
 * day the picker displays. Callers deriving `minDate`/`maxDate` from other form fields must use
 * this too — bare `dayjs(<UTC string>)` resolves to the previous day west of UTC.
 */
export const resolveDatePickerValue = (value: unknown): Dayjs | null => {
  if (typeof value === 'string') {
    const sanitized = sanitizeDateTimezone(value);
    return sanitized ? dayjs(sanitized) : null;
  }
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    // Dates committed by RHFDatePicker are UTC midnight (see getUtcValue): parse the UTC date
    // part as local midnight so the displayed day matches in timezones west of UTC
    // (01.01 -> 31.12 bug). Dates built as local midnight (e.g. `new Date(2030, 0, 1)` form
    // defaults) must stay local, otherwise the same day-shift appears east of UTC.
    const isUtcMidnight = value.getUTCHours() === 0 && value.getUTCMinutes() === 0;
    return isUtcMidnight ? dayjs(value.toISOString().slice(0, 10)) : dayjs(value);
  }
  if (dayjs.isDayjs(value)) return value;
  return null;
};

/**
 * Parses a string representation of a number into a JavaScript `number`.
 * Handles both German (e.g., "1.234,56") and English (e.g., "1,234.56") formats.
 *
 * @param {Object} params - The parameter object.
 * @param {string | undefined} params.value - The string representation of the number to be parsed.
 * @param {number | 'use-value-decimal-places'} [params.maxDecimalPlaces] - The maximum number of decimal places to
 * include in the returned number. The maximum value is 100.
 *   If set to `'use-value-decimal-places'`, the number of decimal places from the original string will be preserved.
 *   If not provided, defaults to 3 decimal places.
 * @returns {number | undefined} The parsed and formatted number, or `undefined` if the input is invalid.
 */
export const parseStringToNumber = ({
  value,
  maxDecimalPlaces,
}: {
  value: string | undefined;
  maxDecimalPlaces?: number | 'use-value-decimal-places';
}): number | undefined => {
  if (value === undefined) return;

  // Check if the value is in German format (1.234,56)
  const isGermanFormat = /^[0-9]+(?:\.[0-9]{3})*(?:,[0-9]+)?$/.test(value);

  // Check if the value is in English format (1,234.56)
  const isEnglishFormat = /^[0-9]+(?:,[0-9]{3})*(?:\.[0-9]+)?$/.test(value);

  let normalizedNumber = value;

  if (isGermanFormat) {
    // Convert German format (1.234,56) → (1234.56)
    normalizedNumber = value.replace(/\./g, '').replace(',', '.');
  } else if (isEnglishFormat) {
    // Convert English format (1,234.56) → (1234.56)
    normalizedNumber = value.replace(/,/g, '');
  }

  // Convert to float
  const parsedValue = parseFloat(normalizedNumber);
  if (isNaN(parsedValue)) return;

  let parsedMaxDecimalPlaces: number;
  if (isNil(maxDecimalPlaces)) {
    parsedMaxDecimalPlaces = 3;
  } else if (maxDecimalPlaces === 'use-value-decimal-places') {
    parsedMaxDecimalPlaces = (normalizedNumber.split('.').at(1) ?? '').length;
  } else {
    parsedMaxDecimalPlaces = maxDecimalPlaces;
  }

  //correctly formatted number
  return +parsedValue.toFixed(clamp(parsedMaxDecimalPlaces, 0, 100));
};
